/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/overflowRowItems.ts
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-OVERFLOWITEMS-001
 * PURPOSE: Generate a More-menu row's caret submenu items from its registry
 *   contextActions — the overflow-row twin of laneMenuItems.ts's lane builder.
 * ROLE IN ARCHITECTURE: Pure builder between the registry contract and
 *   MoreMenu's generic caret affordance (D269 D1).
 * MAIN EXPORTS: buildOverflowRowSubActions, OverflowRowSubActionModel, OverflowRowDeps.
 * NON-RESPONSIBILITIES: No rendering, no mutation calls of its own — callers
 *   (useReaderHeaderCommands) own transports; MoreMenu.tsx owns the DOM.
 * NOTES FOR FUTURE AI: ANY overflow-zone action that declares contextActions
 *   gets a caret in MoreMenu automatically (MoreMenu.tsx checks
 *   subActions.length, never an action.id === 'junk' special case). To wire a
 *   NEW overflow action's contextActions, add its ids to ROW_ITEM_FACTORIES
 *   here and extend OverflowRowDeps + the binding in useReaderHeaderCommands
 *   .tsx — never hand-roll a second caret implementation in MoreMenu.tsx.
 *   Ids absent from ROW_ITEM_FACTORIES, or whose deps are missing, are
 *   OMITTED — same "omit rather than render a dead row" contract
 *   laneMenuItems.ts uses (BUG-2026-07-17-EMAIL-LANE-DEAD-CONTEXTMENU).
 */
import { getReaderAction } from '../actions/registry'
import type { ReaderActionId } from '../model/types'

export interface OverflowRowSubActionModel {
  /** Registry contextActions id this row satisfies. */
  id: string
  label: string
  danger?: boolean
  onClick: () => void
}

export interface OverflowRowDeps {
  /** junk row: retro/prospective auto-trash rule options — present only
   *  when the sender address resolves to a usable match value (mirrors
   *  useJunkActions/JunkButton's `exact`/`domain` derivation). */
  junk?: {
    domain?: string
    exact?: string
    onAutoTrashDomain?: () => void
    onAutoTrashSender?: () => void
  }
}

type RowItemFactory = (deps: OverflowRowDeps) => OverflowRowSubActionModel | null

/**
 * Registry contextActions id -> concrete caret row, given the transports the
 * host actually wired. `junk_this_email` resolves to null BY DESIGN: it is
 * the row's own primary click (bin now immediately), never duplicated inside
 * its own caret — the caret offers only the escalation options.
 */
const ROW_ITEM_FACTORIES: Readonly<Record<string, RowItemFactory>> = {
  junk_this_email: () => null,
  auto_trash_domain: (deps) =>
    deps.junk?.domain && deps.junk.onAutoTrashDomain
      ? {
          id: 'auto_trash_domain',
          label: `Auto-trash domain \u00b7 ${deps.junk.domain}`,
          danger: true,
          onClick: deps.junk.onAutoTrashDomain,
        }
      : null,
  auto_trash_sender: (deps) =>
    deps.junk?.exact && deps.junk.onAutoTrashSender
      ? {
          id: 'auto_trash_sender',
          label: `Just this sender \u00b7 ${deps.junk.exact}`,
          danger: true,
          onClick: deps.junk.onAutoTrashSender,
        }
      : null,
}

/**
 * Build the caret submenu rows for one overflow action's contextActions.
 *
 * Inputs: the owning action's id and the transports the host actually wired.
 * Output: ordered, concrete rows (registry contextActions order preserved);
 * unresolved ids (no factory, or the factory declines given the deps) are
 * omitted. Failure mode: none — this is a pure filter/map, never throws.
 */
export function buildOverflowRowSubActions(
  actionId: ReaderActionId,
  deps: OverflowRowDeps,
): OverflowRowSubActionModel[] {
  const descriptor = getReaderAction(actionId)
  return descriptor.contextActions
    .map((id) => ROW_ITEM_FACTORIES[id]?.(deps) ?? null)
    .filter((item): item is OverflowRowSubActionModel => item != null)
}
